"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { FUSportsLivescoreCompetitionGroup, SearchEntityResult } from "fansunited-data-layer";
import { fetchLivescoreBoard } from "@/features/live-data/livescoreClient";
import { MAX_COMPETITION_IDS, seedMatches, type LivescoreBoardSeed, type LivescoreQuery } from "@/features/live-data/livescoreParams";
import type { LivescoreBoard, LivescoreSport, LivescoreFilter } from "@/views/livescore/utils/livescore";
import { LIVESCORE_REFRESH_INTERVAL_MS } from "@/config/livescore";
import type { LocaleCode } from "@/lib/labels/types";
interface UseLivescoreMatchesOptions {
  sport: LivescoreSport;
  filter: LivescoreFilter;
  date: string;
  locale: LocaleCode;
  /** The user's favourite competition ids; drives the my-leagues stage and the all-tab expansion. */
  favouriteIds: string[];
  /**
   * Stage 2 waits for this: the client switches to my-leagues on first load when the user has
   * favourites, and the whole-day fetch would be wasted (and discarded) under it.
   */
  favouritesLoaded?: boolean;
  /** Server-rendered popular board; used in place of the first stage-1 fetch when it answers the query. */
  initialBoard?: LivescoreBoardSeed;
}
export type StageStatus = "idle" | "loading" | "ready" | "failed";
export interface StageState {
  groups: FUSportsLivescoreCompetitionGroup[];
  status: StageStatus;
}
export interface UseLivescoreMatchesResult {
  /** Stage 1: the popular competitions (or the favourites on my-leagues), fully hydrated. */
  popular: StageState;
  /** Stage 2: the whole day hydrated to competition level; `idle` when the filter does not use it. */
  rest: StageState;
  /** Entities from both stages, merged. Per-group team hydration merges into the shared context, not here. */
  entities: Record<string, SearchEntityResult>;
  /** True only when every stage that runs is ready and none has a group. */
  empty: boolean;
}
const IDLE: StageState = {
  groups: [],
  status: "idle"
};
const EMPTY_ENTITIES: Record<string, SearchEntityResult> = {};

/**
 * The livescore board in two stages, each polled every `LIVESCORE_REFRESH_INTERVAL_MS`.
 *
 * Stage 1 asks for `competitions=popular` (or the first `MAX_COMPETITION_IDS` favourites on
 * my-leagues) at full hydration: small, fast, and the part people came for. It is seeded from
 * the server when `initialBoard` answers the same query, so the first paint needs no fetch.
 *
 * Stage 2 asks for the whole day with `hydrate=competitions`: every group, competition entities
 * only, no team lookups. It starts after mount and renders under the popular block as collapsed
 * headers; teams arrive per group through `useGroupHydration` when a group is opened. It is
 * skipped for the popular and my-leagues filters, which show only stage 1.
 *
 * A failed fetch is a failure, not an empty day: on a refresh the last good groups stay and the
 * status reads `failed`; on a first load the stage is `failed` with no groups. `empty` is true
 * only when every running stage is `ready` with nothing in it. Responses carry a per-stage
 * sequence number so a slow earlier response never overwrites a newer one, and any change to
 * sport, filter, date or locale resets both stages before refetching.
 */
export function useLivescoreMatches({
  sport,
  filter,
  date,
  locale,
  favouriteIds,
  favouritesLoaded = true,
  initialBoard
}: UseLivescoreMatchesOptions): UseLivescoreMatchesResult {
  const stage1Query = stageOneQuery(sport, filter, date, locale, favouriteIds);
  const stage2Query = favouritesLoaded ? stageTwoQuery(sport, filter, date, locale) : null;
  const seeded = stage1Query !== null && seedMatches(initialBoard, stage1Query);
  const [popular, setPopular] = useState<StageState>(seeded ? {
    groups: initialBoard.board.groups,
    status: "ready"
  } : stage1Query ? {
    groups: [],
    status: "loading"
  } : IDLE);
  const [rest, setRest] = useState<StageState>(stage2Query ? {
    groups: [],
    status: "loading"
  } : IDLE);
  const [popularEntities, setPopularEntities] = useState(seeded ? initialBoard.board.entities : EMPTY_ENTITIES);
  const [restEntities, setRestEntities] = useState(EMPTY_ENTITIES);
  // True while stage 1 still shows the board the state initialisers took from the seed: the
  // first effect run skips that fetch once, then every query change fetches as usual.
  const seedAppliedRef = useRef(seeded);
  const seqRef = useRef({
    popular: 0,
    rest: 0
  });
  const stage1Key = stage1Query ? JSON.stringify(stage1Query) : "";
  const stage2Key = stage2Query ? JSON.stringify(stage2Query) : "";
  const runStage = useCallback(async (query: LivescoreQuery, stage: "popular" | "rest", isRefresh: boolean, setStage: (update: (prev: StageState) => StageState) => void, setEntities: (entities: Record<string, SearchEntityResult>) => void) => {
    const seq = ++seqRef.current[stage];
    if (!isRefresh) setStage(() => ({
      groups: [],
      status: "loading"
    }));
    const board: LivescoreBoard | null = await fetchLivescoreBoard(query);
    if (seq !== seqRef.current[stage]) return; // a newer request has superseded this one
    if (board) {
      setStage(() => ({
        groups: board.groups,
        status: "ready"
      }));
      setEntities(board.entities);
    } else {
      setStage(prev => ({
        groups: isRefresh ? prev.groups : [],
        status: "failed"
      }));
    }
  }, []);

  // Stage 1: first load (unless seeded) and whenever its query changes.
  useEffect(() => {
    if (!stage1Query) {
      seqRef.current.popular++; // a response still in flight for the old query must not land
      setPopular(IDLE);
      setPopularEntities(EMPTY_ENTITIES);
      return;
    }
    if (seedAppliedRef.current) {
      seedAppliedRef.current = false;
      return;
    }
    void runStage(stage1Query, "popular", false, setPopular, setPopularEntities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage1Key, runStage]);

  // Stage 2: after mount and whenever its query changes.
  useEffect(() => {
    if (!stage2Query) {
      seqRef.current.rest++;
      setRest(IDLE);
      setRestEntities(EMPTY_ENTITIES);
      return;
    }
    void runStage(stage2Query, "rest", false, setRest, setRestEntities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage2Key, runStage]);

  // Polling for whichever stages are active.
  useEffect(() => {
    const interval = setInterval(() => {
      if (stage1Query) void runStage(stage1Query, "popular", true, setPopular, setPopularEntities);
      if (stage2Query) void runStage(stage2Query, "rest", true, setRest, setRestEntities);
    }, LIVESCORE_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage1Key, stage2Key, runStage]);

  // Memoised: consumers publish this into context with an effect keyed on identity.
  const entities = useMemo(() => ({
    ...restEntities,
    ...popularEntities
  }), [restEntities, popularEntities]);
  const running = [popular, rest].filter(s => s.status !== "idle");
  const empty = running.length > 0 && running.every(s => s.status === "ready" && s.groups.length === 0);
  return {
    popular,
    rest,
    entities,
    empty
  };
}
function stageOneQuery(sport: LivescoreSport, filter: LivescoreFilter, date: string, locale: LocaleCode, favouriteIds: string[]): LivescoreQuery | null {
  if (filter === "my-leagues") {
    if (favouriteIds.length === 0) return null;
    return {
      sport,
      filter,
      date,
      locale,
      competitions: favouriteIds.slice(0, MAX_COMPETITION_IDS)
    };
  }
  return {
    sport,
    filter,
    date,
    locale,
    competitions: "popular"
  };
}
function stageTwoQuery(sport: LivescoreSport, filter: LivescoreFilter, date: string, locale: LocaleCode): LivescoreQuery | null {
  if (filter === "popular" || filter === "my-leagues") return null;
  return {
    sport,
    filter,
    date,
    locale,
    hydrate: "competitions"
  };
}